import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { OutputConfig, OutputType, GroupByField, TimeGranularity, TrendMetric } from '../../types/analytics.types';
import { useCatalogStore } from '../../stores/catalogStore';

interface OutputSelectorProps {
  config: OutputConfig;
  onChange: (config: Partial<OutputConfig>) => void;
}

const OUTPUT_TYPE_OPTIONS: Array<{ value: OutputType; label: string; description: string }> = [
  { value: 'count', label: 'Count patients', description: 'Single number showing total matches' },
  { value: 'list', label: 'List patients', description: 'Table of matching patients' },
  { value: 'trend', label: 'Show trend', description: 'Line chart over time' },
  { value: 'breakdown', label: 'Break down by...', description: 'Bar/donut chart by category' },
];

const GRANULARITY_OPTIONS: Array<{ value: TimeGranularity; label: string }> = [
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
  { value: 'quarter', label: 'Quarterly' },
  { value: 'year', label: 'Yearly' },
];

export function OutputSelector({ config, onChange }: OutputSelectorProps) {
  const { groupBy: groupByOptions, trendMetrics } = useCatalogStore();

  const handleTypeChange = (type: OutputType) => {
    const updates: Partial<OutputConfig> = { type };

    if (type === 'breakdown' && !config.groupBy) {
      updates.groupBy = (groupByOptions[0]?.value as GroupByField) || 'pcp';
    }

    if (type === 'trend' && !config.metric) {
      updates.metric = (trendMetrics[0]?.value as TrendMetric) || 'consents_obtained';
    }

    if (type === 'trend' && !config.timeRange) {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 6);

      updates.timeRange = {
        start: startDate.toISOString(),
        end: endDate.toISOString(),
        granularity: 'month',
      };
    }

    onChange(updates);
  };

  return (
    <div className="space-y-4 p-4 rounded-lg border bg-muted/30">
      <div className="text-sm font-medium">Output</div>

      <RadioGroup
        value={config.type}
        onValueChange={(value) => handleTypeChange(value as OutputType)}
        className="grid grid-cols-2 gap-2"
      >
        {OUTPUT_TYPE_OPTIONS.map((option) => (
          <div key={option.value} className="flex items-start space-x-2">
            <RadioGroupItem value={option.value} id={option.value} className="mt-1" />
            <Label htmlFor={option.value} className="cursor-pointer">
              <div className="font-medium">{option.label}</div>
              <div className="text-xs text-muted-foreground">{option.description}</div>
            </Label>
          </div>
        ))}
      </RadioGroup>

      {config.type === 'breakdown' && (
        <div className="flex flex-wrap items-center gap-2 pt-2">
          <Label className="text-sm">Group by:</Label>
          <Select
            value={config.groupBy}
            onValueChange={(value) => onChange({ groupBy: value as GroupByField })}
          >
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Select field..." />
            </SelectTrigger>
            <SelectContent>
              {groupByOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {config.type === 'trend' && (
        <div className="flex flex-wrap items-center gap-4 pt-2">
          <div className="flex flex-wrap items-center gap-2">
            <Label className="text-sm">Count:</Label>
            <Select
              value={config.metric || 'consents_obtained'}
              onValueChange={(value) => onChange({ metric: value as TrendMetric })}
            >
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {trendMetrics.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Label className="text-sm">Granularity:</Label>
            <Select
              value={config.timeRange?.granularity || 'month'}
              onValueChange={(value) =>
                onChange({
                  timeRange: {
                    ...config.timeRange!,
                    granularity: value as TimeGranularity,
                  },
                })
              }
            >
              <SelectTrigger className="w-full sm:w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GRANULARITY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </div>
  );
}
